/**
 * Typed wrappers for the chat attachment endpoints.
 *
 *   POST /api/attachment/upload   multipart `file` → { status, attachment }
 *   GET  /api/attachment/<id>     image bytes (consumed by <img src>)
 *
 * The endpoints live in the legacy /api/* namespace - same reasoning
 * as /api/chat. Re-exposing under /api/platform/* would duplicate
 * the AttachmentService gating logic (owner-only, per-tenant
 * client_id, file-size + mime-type validation) for no benefit.
 */

import { ApiError } from './client';

/** Attachment metadata returned by the upload endpoint. The id is
 *  what we ship to /api/chat as `attachment_ids: [<id>]`. Other
 *  fields are surfaced to the staging UI so it can show a name +
 *  human-friendly size. */
export interface AttachmentMeta {
  id: string;
  original_name?: string;
  mime?: string;
  size_bytes?: number;
}

interface UploadEnvelope {
  status: 'success';
  attachment: AttachmentMeta;
}

interface UploadErrorEnvelope {
  status: 'error';
  code: string;
  message: string;
}

/** Upload a single image attachment. Multipart - uses fetch
 *  directly rather than the JSON `api()` helper because the
 *  request body is a FormData, not JSON. */
export async function uploadAttachment(file: File): Promise<AttachmentMeta> {
  const fd = new FormData();
  fd.append('file', file, file.name);

  const res = await fetch('/api/attachment/upload', {
    method: 'POST',
    credentials: 'include',
    body: fd,
  });

  // Best-effort JSON parse; the endpoint always returns JSON, even
  // on 4xx, but we guard against an HTML error page coming back
  // from a misconfigured proxy.
  let payload: unknown = null;
  try {
    payload = await res.json();
  } catch {
    /* Non-JSON error body - fall through. */
  }

  if (!res.ok || (payload as UploadErrorEnvelope | null)?.status === 'error') {
    const obj = (payload as UploadErrorEnvelope | null) ?? null;
    throw new ApiError(
      res.status,
      obj?.code ?? 'UPLOAD_FAILED',
      obj?.message ?? `Upload failed (${res.status})`,
    );
  }

  return (payload as UploadEnvelope).attachment;
}

/** Local URL for an attachment id. Used for <img src> in the
 *  message bubbles AND for the lightbox. */
export function attachmentUrl(id: string): string {
  return `/api/attachment/${encodeURIComponent(id)}`;
}
